import React, { useState, useMemo, useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Trash2, Plus, Edit2, Check, X, Loader2, Settings, ChevronDown, ChevronRight, Play, Pause, Wrench } from 'lucide-react';
import type { BaseCustomProps } from './types';
import { useToolState } from '@/contexts/ToolStateContext';
import { useToolActions } from '@/contexts/ToolActionsContext';
import { useMessageContext } from '@/contexts/MessageContext';
import { useMessageState } from '@/contexts/MessageStateContext';
import type { FirstImpressionTool } from '@/types';
import type { SelectedItem } from '@/types/file-browser';

// Import types from shared types file
type ConfigParam = import('@/types').ConfigParam;

interface ToolConfirmationProps extends BaseCustomProps {
  tools_to_use?: FirstImpressionTool[] | string;
}

export const ToolConfirmation: React.FC<ToolConfirmationProps> = ({ 
  children, 
  className,
  tools_to_use = [],
  ...props
}) => {
  // Parse tools_to_use if it's a JSON string
  const { parsedTools, originalCounts } = useMemo(() => {
    try {
      let rawTools: FirstImpressionTool[] = [];
      if (typeof tools_to_use === 'string') {
        // HTML unescape the string first in case it was escaped for HTML attributes
        const unescapedString = tools_to_use
          .replace(/&quot;/g, '"')
          .replace(/&#x27;/g, "'")
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&amp;/g, '&'); // This should be last to avoid double-unescaping
        
        rawTools = JSON.parse(unescapedString) as FirstImpressionTool[];
      } else {
        rawTools = tools_to_use;
      }

      // Keep tools separate - don't group them anymore since backend handles splitting
      const toolCounts: Record<string, number> = {};
      
      rawTools.forEach(tool => {
        toolCounts[tool.name] = (toolCounts[tool.name] || 0) + 1;
      });

      return {
        parsedTools: rawTools,
        originalCounts: toolCounts
      };
    } catch (error) {
      console.error('Error parsing tools_to_use:', error, 'Original string:', tools_to_use);
      return { parsedTools: [], originalCounts: {} };
    }
  }, [tools_to_use]);

  // Use optimized contexts for tool confirmation state
  const {
    isWaitingForConfirmation,
    confirmTools,
    rejectTools,
    setPendingConfirmation,
    confirmationId: activeConfirmationId,
    hasProcessedSignature,
    markSignatureProcessed,
    clearProcessedSignature
  } = useToolState();

  const {
    isProcessingConfirmation
  } = useToolActions();

  // Use message context to determine if this is the last message
  const { isLastMessage } = useMessageContext();
  
  // Get selectedItems from optimized MessageStateContext
  const { selectedItems } = useMessageState();

  const [tools, setTools] = useState<FirstImpressionTool[]>(parsedTools);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingConfigIndex, setEditingConfigIndex] = useState<number | null>(null);
  const [tempValue, setTempValue] = useState('');
  const [expandedTools, setExpandedTools] = useState<Set<number>>(new Set());
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [confirmationId] = useState(() => `confirmation-${Date.now()}-${Math.random().toString(36).substring(7)}`);
  const [hasPendingConfirmation, setHasPendingConfirmation] = useState(false);
  
  // Create a unique identifier for this specific tool configuration to prevent duplicates
  const toolsSignature = useMemo(() => {
    return JSON.stringify(parsedTools);
  }, [parsedTools]);

  // Update tools state when parsedTools changes
  useEffect(() => {
    setTools(parsedTools);
    // Reset the pending confirmation flag if tools change
    setHasPendingConfirmation(false);
    
    // Auto-expand logic - only when in advanced mode
    if (showAdvanced) {
      const autoExpandIndices = new Set<number>();
      
      // Auto-expand first tool if only one tool
      if (parsedTools.length === 1) {
        autoExpandIndices.add(0);
      }
      
      // Auto-expand file-browser tools that have selected items
      parsedTools.forEach((tool, index) => {
        if (tool.name === 'file-browser' && selectedItems && selectedItems.length > 0) {
          autoExpandIndices.add(index);
        }
      });
      
      setExpandedTools(autoExpandIndices);
    } else {
      setExpandedTools(new Set());
    }
  }, [parsedTools, selectedItems, showAdvanced]);

  // Set pending confirmation when component mounts with tools (only once)
  // Only set confirmation for the last message
  useEffect(() => {
    if (parsedTools.length > 0 && 
        isLastMessage &&
        !isProcessingConfirmation && 
        !hasPendingConfirmation && 
        !activeConfirmationId) { // Don't set if there's already an active confirmation
      
      // Check if we've already processed this exact tool configuration
      if (hasProcessedSignature(toolsSignature)) {
        console.log('Skipping duplicate ToolConfirmation with signature:', toolsSignature);
        return;
      }
      
      // Mark this signature as processed
      markSignatureProcessed(toolsSignature);
      setPendingConfirmation(parsedTools, confirmationId);
      setHasPendingConfirmation(true);
    }
  }, [parsedTools, isLastMessage, setPendingConfirmation, confirmationId, isProcessingConfirmation, hasPendingConfirmation, activeConfirmationId, toolsSignature, hasProcessedSignature, markSignatureProcessed]);

  // Cleanup effect - if component unmounts without confirmation/rejection, clear the signature
  useEffect(() => {
    return () => {
      // Only clear if this confirmation wasn't processed
      if (hasPendingConfirmation && !isWaitingForConfirmation) {
        clearProcessedSignature(toolsSignature);
      }
    };
  }, [toolsSignature, hasPendingConfirmation, isWaitingForConfirmation, clearProcessedSignature]);

  const toggleToolExpanded = (index: number) => {
    const newExpanded = new Set(expandedTools);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedTools(newExpanded);
  };

  // Component to render selected files for file-browser tool
  const renderSelectedFiles = (toolName: string) => {
    if (toolName !== 'file-browser' || !selectedItems || selectedItems.length === 0) {
      return null;
    }

    return (
      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
        <div className="text-xs font-medium text-blue-800 mb-2">
          Selected Files ({selectedItems.length}):
        </div>
        <div className="space-y-1">
          {selectedItems.map((item, index) => (
            <div key={index} className="flex items-center gap-2 text-xs">
              <Badge 
                variant="outline" 
                className="text-xs bg-white border-blue-300 text-blue-700"
              >
                {item.type}
              </Badge>
              <span className="text-blue-800 font-mono bg-white px-2 py-1 rounded border border-blue-200">
                {item.path}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const handleDeleteTool = (index: number) => {
    setTools(tools.filter((_, i) => i !== index));
  };

  const handleDeleteConfig = (toolIndex: number, configIndex: number) => {
    const updatedTools = [...tools];
    updatedTools[toolIndex].config = updatedTools[toolIndex].config.filter((_, i) => i !== configIndex);
    setTools(updatedTools);
  };

  const handleEditConfig = (toolIndex: number, configIndex: number) => {
    setEditingIndex(toolIndex);
    setEditingConfigIndex(configIndex);
    setTempValue(tools[toolIndex].config[configIndex].value);
  };

  const handleSaveEdit = () => {
    if (editingIndex !== null && editingConfigIndex !== null) {
      const updatedTools = [...tools];
      updatedTools[editingIndex].config[editingConfigIndex].value = tempValue;
      setTools(updatedTools);
      setEditingIndex(null);
      setEditingConfigIndex(null);
      setTempValue('');
    }
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditingConfigIndex(null);
    setTempValue('');
  };

  const handleAddConfig = (toolIndex: number) => {
    const updatedTools = [...tools];
    updatedTools[toolIndex].config.push({ parameter: 'query', value: '' });
    setTools(updatedTools);
    // Immediately start editing the new config
    setEditingIndex(toolIndex);
    setEditingConfigIndex(updatedTools[toolIndex].config.length - 1);
    setTempValue('');
    // Ensure tool is expanded
    setExpandedTools(prev => new Set([...prev, toolIndex]));
  };

  const handleConfirm = () => {
    console.log('Confirming tools:', tools);
    confirmTools(tools);
    // Remove this signature from processed set after confirmation
    clearProcessedSignature(toolsSignature);
  };

  const handleReject = () => {
    console.log('Rejecting tools');
    rejectTools();
    // Remove this signature from processed set after rejection
    clearProcessedSignature(toolsSignature);
  };

  // Show processing state when confirmation is being processed
  if (isProcessingConfirmation) {
    return (
      <div className={twMerge(
        "inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-700",
        className
      )}>
        <Loader2 className="w-4 h-4 animate-spin" />
        <span>Running tools...</span>
      </div>
    );
  }

  // Compact display for read-only (non-last messages)
  if (!isLastMessage) {
    return (
      <div className={twMerge(
        "inline-block max-w-full",
        className
      )}>
        <div className="inline-flex flex-wrap items-center gap-2 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <Wrench className="w-4 h-4 flex-shrink-0" />
            <span className="whitespace-nowrap">Used tools:</span>
          </div>
          <div className="flex flex-wrap gap-1">
            {tools.map((tool, index) => (
              <Badge key={index} variant="secondary" className="text-xs">
                {tool.name}
                {originalCounts[tool.name] > 1 && (
                  <span className="ml-1 text-xs opacity-60">×{originalCounts[tool.name]}</span>
                )}
              </Badge>
            ))}
          </div>
          <Button
            onClick={() => setShowAdvanced(!showAdvanced)}
            size="sm"
            variant="ghost"
            className="h-6 px-2 text-xs text-gray-600 hover:bg-gray-100 ml-auto"
          >
            <Settings className="w-4 h-4" />
          </Button>
        </div>
        
        {/* Advanced configuration panel for used tools */}
        {showAdvanced && (
          <div className="mt-2 p-3 bg-white border border-gray-200 rounded-md shadow-sm overflow-x-auto">
            <div className="text-xs font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Tool Configuration (Read-only)
            </div>
            
            <div className="space-y-2 min-w-0">
              {tools.map((tool, toolIndex) => {
                const isExpanded = expandedTools.has(toolIndex);
                return (
                  <div key={toolIndex} className="border border-gray-200 rounded-md">
                    <div className="flex items-center justify-between p-2">
                      <button
                        onClick={() => toggleToolExpanded(toolIndex)}
                        className="flex items-center gap-2 text-left hover:text-gray-700 transition-colors min-w-0"
                      >
                        {isExpanded ? (
                          <ChevronDown className="w-3 h-3 flex-shrink-0" />
                        ) : (
                          <ChevronRight className="w-3 h-3 flex-shrink-0" />
                        )}
                        <Badge variant="secondary" className="text-xs">
                          {tool.name}
                        </Badge>
                      </button>
                      
                      <div className="flex items-center gap-1 ml-2">
                        {originalCounts[tool.name] > 1 && (
                          <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600 border-blue-200">
                            {originalCounts[tool.name]}x
                          </Badge>
                        )}
                        <span className="text-xs text-gray-500 whitespace-nowrap">
                          {tool.config.length} config{tool.config.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="border-t border-gray-200 p-2 pt-1 space-y-1">
                        {tool.config.map((config, configIndex) => (
                          <div key={configIndex} className="flex items-start gap-2 text-xs">
                            <span className="text-gray-600 min-w-[4rem] flex-shrink-0 font-medium">
                              {config.parameter}:
                            </span>
                            <span className="text-gray-800 flex-1 break-words bg-gray-50 px-2 py-1 rounded text-xs">
                              {config.value || <span className="text-gray-400 italic">empty</span>}
                            </span>
                          </div>
                        ))}
                        
                        {tool.config.length === 0 && (
                          <div className="text-xs text-gray-400 italic py-1">
                            No configurations
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex justify-end pt-2 mt-2 border-t border-gray-200">
              <Button
                onClick={() => setShowAdvanced(false)}
                size="sm"
                variant="ghost"
                className="text-gray-600 hover:bg-gray-100"
              >
                Close
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // No tools state
  if (tools.length === 0) {
    return (
      <div className={twMerge(
        "inline-flex items-center gap-2 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-500",
        className
      )}>
        <Wrench className="w-4 h-4" />
        <span>No tools needed</span>
        <Button 
          onClick={handleConfirm}
          size="sm"
          variant="ghost"
          className="h-6 px-2 text-xs bg-green-100 hover:bg-green-200 text-green-700"
        >
          Continue
        </Button>
      </div>
    );
  }

  // Main interactive component for last message
  return (
    <div className={twMerge(
      "inline-block max-w-full",
      className
    )}>
      {/* Compact header with tools and primary actions */}
      <div className="inline-flex flex-wrap items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-md text-sm">
        <div className="flex items-center gap-2">
          <Play className="w-4 h-4 text-blue-600 flex-shrink-0" />
          <span className="text-blue-800 font-medium whitespace-nowrap">Tools ready:</span>
        </div>
        
        <div className="flex flex-wrap gap-1">
          {tools.map((tool, index) => (
            <Badge key={index} variant="outline" className="text-xs bg-white border-blue-300 text-blue-700">
              {tool.name}
              {originalCounts[tool.name] > 1 && (
                <span className="ml-1 text-xs opacity-60">×{originalCounts[tool.name]}</span>
              )}
            </Badge>
          ))}
        </div>

        <div className="flex items-center gap-1 ml-auto">
          <Button 
            onClick={handleConfirm}
            size="sm"
            className="h-6 px-3 text-xs bg-green-600 hover:bg-green-700 text-white"
          >
            Run
          </Button>
          
          <Button
            onClick={() => setShowAdvanced(!showAdvanced)}
            size="sm"
            variant="ghost"
            className="h-6 px-2 text-xs text-blue-600 hover:bg-blue-100"
          >
            <Settings className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Advanced configuration panel (hidden by default) */}
      {showAdvanced && (
        <div className="mt-2 p-3 bg-white border border-gray-200 rounded-md shadow-sm overflow-x-auto">
          <div className="text-xs font-medium text-gray-700 mb-2 flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Advanced Configuration
          </div>
          
          <div className="space-y-2 min-w-0">
            {tools.map((tool, toolIndex) => {
              const isExpanded = expandedTools.has(toolIndex);
              return (
                <div key={toolIndex} className="border border-gray-200 rounded-md">
                  {/* Tool Header */}
                  <div className="flex items-center justify-between p-2">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <button
                        onClick={() => toggleToolExpanded(toolIndex)}
                        className="flex items-center gap-2 text-left hover:text-gray-700 transition-colors min-w-0"
                      >
                        {isExpanded ? (
                          <ChevronDown className="w-3 h-3 flex-shrink-0" />
                        ) : (
                          <ChevronRight className="w-3 h-3 flex-shrink-0" />
                        )}
                        <Badge variant="secondary" className="text-xs">
                          {tool.name}
                        </Badge>
                      </button>
                      
                      <div className="flex items-center gap-1 ml-2">
                        {originalCounts[tool.name] > 1 && (
                          <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600 border-blue-200">
                            {originalCounts[tool.name]}x
                          </Badge>
                        )}
                        <span className="text-xs text-gray-500 whitespace-nowrap">
                          {tool.config.length} config{tool.config.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleAddConfig(toolIndex)}
                        className="h-6 w-6 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                      >
                        <Plus className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteTool(toolIndex)}
                        className="h-6 w-6 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>

                  {/* Tool Configurations */}
                  {isExpanded && (
                    <div className="border-t border-gray-200 p-2 pt-1 space-y-1">
                      {tool.config.map((config, configIndex) => (
                        <div key={configIndex} className="flex items-start gap-2 text-xs">
                          <span className="text-gray-600 min-w-[4rem] flex-shrink-0 font-medium">
                            {config.parameter}:
                          </span>
                          
                          {editingIndex === toolIndex && editingConfigIndex === configIndex ? (
                            <div className="flex items-center gap-1 flex-1">
                              <Input
                                value={tempValue}
                                onChange={(e) => setTempValue(e.target.value)}
                                className="text-xs h-6"
                                autoFocus
                              />
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={handleSaveEdit} 
                                className="h-6 w-6 p-0 text-green-600 hover:bg-green-50"
                              >
                                <Check className="w-3 h-3" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={handleCancelEdit} 
                                className="h-6 w-6 p-0 text-red-600 hover:bg-red-50"
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-start gap-1 flex-1">
                              <span className="text-gray-800 flex-1 break-words bg-gray-50 px-2 py-1 rounded text-xs">
                                {config.value || <span className="text-gray-400 italic">empty</span>}
                              </span>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => handleEditConfig(toolIndex, configIndex)}
                                className="h-6 w-6 p-0 text-blue-600 hover:bg-blue-50"
                              >
                                <Edit2 className="w-3 h-3" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                onClick={() => handleDeleteConfig(toolIndex, configIndex)}
                                className="h-6 w-6 p-0 text-red-600 hover:bg-red-50"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                      
                      {tool.config.length === 0 && (
                        <div className="text-xs text-gray-400 italic py-1">
                          No configurations - click + to add one
                        </div>
                      )}
                      
                      {/* Show selected files for file-browser tool */}
                      {renderSelectedFiles(tool.name)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Advanced actions */}
          <div className="flex gap-2 pt-2 mt-2 border-t border-gray-200">
            <Button 
              onClick={handleConfirm}
              size="sm"
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <Check className="w-4 h-4 mr-1" />
              Run Tools
            </Button>
            <Button 
              onClick={handleReject}
              size="sm"
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50"
            >
              <X className="w-4 h-4 mr-1" />
              Skip All
            </Button>
            <Button
              onClick={() => setShowAdvanced(false)}
              size="sm"
              variant="ghost"
              className="text-gray-600 hover:bg-gray-100"
            >
              Close
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ToolConfirmation;